import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Language } from "../../../utils";
import { user as userActions } from "../../../actions";
import makeClasses from "../styles";
import Grid from "@material-ui/core/Grid";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { InputLabel } from "@material-ui/core";
import clsx from "clsx";
import { Button } from "../../../components";
import { toast } from "react-toastify";

const StepSix = ({
  history,
  nextStep,
  prevStep,
  handleChange,
  traceId,
  stepOneData,
}) => {
  const dispatch = useDispatch();
  const classes = makeClasses();

  const { loading, languageTexts } = useSelector((state) => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));

  const i18n = Language(languageTexts);

  const [countries, setCountries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [cities, setCities] = useState([]);
  const [inputCountry, setInputCountry] = useState(null);
  const [inputDepartment, setInputDepartment] = useState(null);
  const [inputCity, setInputCity] = useState(null);
  const [inputAddress, setInputAddress] = useState("");
  const [inputAddressNumber, setInputAddressNumber] = useState("");
  const [inputAddressApartment, setInputAddressApartment] = useState("");
  const [inputAddressCorner, setInputAddressCorner] = useState("");
  const [inputMonthlyIncome, setInputMonthlyIncome] = useState("");

  const canMoveToNext =
    inputMonthlyIncome.trim().length > 0 &&
    inputDepartment !== null &&
    inputCity !== null &&
    inputAddress.trim().length > 0 &&
    inputAddressNumber.trim().length > 0 &&
    inputAddressCorner.trim().length > 0;

  useEffect(() => {
    dispatch(
      userActions.getCountries((countries) => {
        setCountries(countries);
        countries.forEach((country) => {
          if (country.alphaCode2 === "UY") {
            setInputCountry(country.id);
          }
        });
      }),
    );
  }, []);

  useEffect(() => {
    if (inputCountry) {
      dispatch(
        userActions.getDepartmentsByCountry(inputCountry, (departments) => {
          setDepartments(departments);
        }),
      );
    }
  }, [inputCountry]);

  useEffect(() => {
    if (inputDepartment) {
      dispatch(
        userActions.getCitiesByDepartments(inputDepartment, (cities) => {
          setCities(cities);
        }),
      );
    }
  }, [inputDepartment]);

  const _getOptionLabel = (array) => (option) => {
    let foundOption = null;
    if (array === "countries") {
      foundOption = countries.find((c) => c.id === option);
      if (foundOption) return foundOption.name;
    } else if (array === "departments") {
      foundOption = departments.find((d) => d.id === option);
      if (foundOption) return foundOption.name;
    } else if (array === "cities") {
      foundOption = cities.find((c) => c.id === option);
      if (foundOption) return foundOption.name;
    }
    if (foundOption) {
      return foundOption.name;
    }
    return "";
  };

  const _handleOnChangeCountry = (e, newValue) => {
    setInputCountry(newValue);
  };

  const _handleOnChangeDepartment = (e, newValue) => {
    setInputDepartment(newValue);
  };

  const _handleOnChangeCity = (e, newValue) => {
    setInputCity(newValue);
  };

  const handleNext = () => {
    const parsedMonthlyIncome = parseFloat(inputMonthlyIncome);
    if (inputAddress.trim().length === 0) {
      return toast.error(
        i18n.get("LegalEntityRegistration.RegisterInvalidAddress"),
        { autoClose: 5000 },
      );
    }

    if (
      !inputMonthlyIncome ||
      isNaN(parsedMonthlyIncome) ||
      parsedMonthlyIncome < 1
    ) {
      return toast.error(
        i18n.get("LegalEntityRegistration.RegisterInvalidIncome"),
        { autoClose: 5000 },
      );
    }

    const address = {
      cityId: inputCity,
      street: inputAddress.trim(),
      corner: inputAddressCorner.trim(),
      postalCode: null,
      number: inputAddressNumber.trim(),
      apartment: inputAddressApartment.trim(),
      description: null,
      longitude: 0,
      latitude: 0,
    };

    dispatch(
      userActions.merchantAffiliationSaveMerchantData(
        traceId,
        {
          address: address,
          monthlyIncome: parsedMonthlyIncome,
        },
        (result) => {
          if (result === "ok") {
            nextStep({
              countryId: inputCountry,
              departmentId: inputDepartment,
              cityId: inputCity,
              address: inputAddress,
              addressNumber: inputAddressNumber,
              addressApartment: inputAddressApartment,
              addressCorner: inputAddressCorner,
              monthlyIncome: inputMonthlyIncome,
            });
          }
        },
      ),
    );
  };

  return (
    <React.Fragment>
      <div className={classes.formContent}>
        <div className={classes.formTitleContainer}>
          <div className={classes.formTitle}>
            {i18n.get("LegalEntityRegistration.StepFormTitle")}
          </div>
          <div className={classes.formText}>
            {i18n.get("LegalEntityRegistration.Step4FormText")}
          </div>
        </div>
        <form layout="vertical" onSubmit={handleNext} className={classes.form}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.MonthlyIncome")}
              </InputLabel>
              <TextField
                type="number"
                placeholder={i18n.get("LegalEntityRegistration.MonthlyIncomePlaceholder")}
                className={classes.formInput}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === "" ||
                    (/^\d+$/.test(value) && parseInt(value) > 0)
                  ) {
                    setInputMonthlyIncome(value);
                  }
                }}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputMonthlyIncome}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("Register.InputCountryTitle")}
              </InputLabel>
              <Autocomplete
                options={countries.map((c) => c.id)}
                getOptionLabel={_getOptionLabel("countries")}
                onChange={_handleOnChangeCountry}
                classes={{ inputRoot: classes.formInputSelect }}
                disabled
                value={inputCountry}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get("Register.InputCountryTitle")}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("Register.InputDepartmentLabel")}
              </InputLabel>
              <Autocomplete
                options={departments.map((d) => d.id)}
                getOptionLabel={_getOptionLabel("departments")}
                onChange={_handleOnChangeDepartment}
                classes={{ inputRoot: classes.formInputSelect }}
                disabled={!inputCountry}
                value={inputDepartment}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get("Register.InputDepartmentTitle")}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputCityTitle")}
              </InputLabel>
              <Autocomplete
                options={cities.map((c) => c.id)}
                getOptionLabel={_getOptionLabel("cities")}
                onChange={_handleOnChangeCity}
                classes={{ inputRoot: classes.formInputSelect }}
                disabled={!inputDepartment}
                value={inputCity}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get(
                      "LegalEntityRegistration.InputCityTitle",
                    )}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputStreetTitle")}
              </InputLabel>
              <TextField
                placeholder={i18n.get(
                  "LegalEntityRegistration.InputStreetTitle",
                )}
                className={classes.formInput}
                onChange={(e) => setInputAddress(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputAddress}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputNumberTitle")}
              </InputLabel>
              <TextField
                placeholder={i18n.get(
                  "LegalEntityRegistration.InputNumberTitle",
                )}
                className={classes.formInput}
                onChange={(e) => setInputAddressNumber(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputAddressNumber}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputApartmentTitle")}
              </InputLabel>
              <TextField
                placeholder={i18n.get(
                  "LegalEntityRegistration.InputApartmentTitle",
                )}
                className={classes.formInput}
                onChange={(e) => setInputAddressApartment(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputAddressApartment}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputCornerTitle")}
              </InputLabel>
              <TextField
                placeholder={i18n.get(
                  "LegalEntityRegistration.InputCornerTitle",
                )}
                className={classes.formInput}
                onChange={(e) => setInputAddressCorner(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputAddressCorner}
              />
            </Grid>
          </Grid>
        </form>
      </div>
      <div className={classes.termsWrapper}>
        <div
          className={clsx(
            classes.formSubmitContainer,
            classes.formSubmitContainerWithOneButton,
            classes.formSubmitContainerWithNoMargin,
          )}>
          <Button
            className={classes.formSubmitButton}
            disabled={loading || !canMoveToNext}
            onClick={handleNext}>
            {i18n.get("LegalEntityRegistration.NextButton")}
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
};
StepSix.id = "com.Handy.LegalEntityRegistration.StepSix";
export default StepSix;
